import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getConnectCardTags, getWidgetTags } from 'services/tags.service';
import useRequest from './useRequest';

/**
 * Custom hook for managing tags with different object types
 * @param {string} objectType - The type of object ('connect_card' or 'connect_card_collection' aka widgets)
 * @param {string|null} objectId - Optional object ID
 * @returns {Object} - { tags: string[], loading: boolean, error: string|null, refetch: function }
 */
const useTags = (objectType, objectId = null) => {
  const account = useSelector((s) => s.settings.account);
  
  // Determine which service function to use based on object type
  const serviceFunction = useMemo(() => {
    switch (objectType) {
      case 'connect_card':
        return getConnectCardTags;
      case 'connect_card_collection':
        return getWidgetTags;
      default:
        throw new Error(`Unsupported object type: ${objectType}`);
    }
  }, [objectType]);

  // Prepare parameters for the API call
  const params = useMemo(() => {
    if (!account?.account_id) {
      return null;
    }
    return objectId ? [account.account_id, objectId] : [account.account_id];
  }, [account?.account_id, objectId]);

  // Use useRequest to handle the API lifecycle
  const [data, { loading, error, refetch }] = useRequest(
    serviceFunction,
    params,
    { 
      skip: !account?.account_id,
      onComplete: (result) => {
        // Optional: Add any post-processing logic here if needed
      }
    }
  );

  // Transform the data to extract tag names
  const tags = useMemo(() => {
    if (!data) return [];
    return data.map(tag => typeof tag === 'string' ? tag : tag.tag);
  }, [data]);

  return {
    tags,
    loading,
    error,
    refetch
  };
};

export default useTags;